import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  unwrapResult,
} from '@reduxjs/toolkit';
import { isEqual, remove } from 'lodash';
import {
  createDefaultJourney,
  createJourney,
  deleteJourney,
  getContactPipelines,
  getJourneyConditionsSchema,
  getJourneys,
  getOrgAnalytics,
  updateJourney,
} from 'shared/api';
import {
  Journey,
  JourneyVersion,
  JourneyGraphNodeQuestion,
  JourneyGraphNodeType,
  JourneyQuestionType,
  Page,
  PartialJourneyWithId,
} from 'types';
import { LBBPipeline } from 'sb-lib/types';
import { AppState } from './reducers';
import { useAppDispatch, useAppSelector } from './store';
import {
  journeyForServerUpdate,
  journeyFromTemplate,
  makeStepLabelMap,
} from 'shared/utils/journey';
import { useCallback } from 'react';
import {
  BusinessRulesConditionsSchema,
  BusinessRulesConditionsSchemaVariable,
  BusinessRulesRuleName,
  BusinessRulesVariableOption,
  BusinessRulesVariableType,
} from 'sb-lib/types/business-rules-types';
import { formatISO, sub } from 'date-fns';

export const initialState = {
  isFetchingJourneyConditionsSchema: false,
  hasFetchedJourneyConditionsSchema: false,
  fetchJourneyConditionsSchemaError: null as null | string,
  journeyConditionsSchema: null as null | BusinessRulesConditionsSchema,

  isFetchingJourneys: false,
  hasFetchedJourneys: false,
  fetchJourneysError: null as null | string,
  journeys: [] as Journey[],

  isSavingNewJourney: false,
  saveNewJourneyError: null as null | string,

  isSavingJourney: false,
  saveJourneyError: null as null | string,

  isRemovingJourney: false,
  removeJourneyError: null as null | string,

  isSavingActiveJourney: false,
  saveActiveJourneyError: null as null | string,
  activeJourneyId: null as null | Journey['id'],
  activeJourney: null as null | (Journey & { hasChanged: boolean }),

  isFetchingContactPipelines: false,
  contactPipelines: [] as LBBPipeline[],

  testDomains: [] as string[],
};

export type JourneyState = typeof initialState;

export const saveNewJourney = createAsyncThunk<
  Journey,
  Pick<Journey, 'name'>,
  { state: AppState }
>('journey/saveNewJourney', async newJourney => {
  return (await createDefaultJourney(newJourney.name)).data;
});

export const saveActiveJourney = createAsyncThunk<
  Journey | void,
  void,
  { state: AppState }
>('journey/saveActiveJourney', async (_, { getState }) => {
  const activeJourney = getState().journey.activeJourney;
  if (activeJourney) {
    return (await updateJourney(journeyForServerUpdate(activeJourney))).data;
  }
});

export const saveJourney = createAsyncThunk<
  Journey,
  PartialJourneyWithId,
  { state: AppState }
>('journey/saveJourney', async journey => {
  return (await updateJourney(journeyForServerUpdate(journey))).data;
});

export const removeJourney = createAsyncThunk<
  Journey['id'],
  Journey['id'],
  { state: AppState }
>('journey/removeJourney', async id => {
  await deleteJourney(id);
  return id;
});

export const cloneActiveJourney = createAsyncThunk<
  Journey | void,
  void,
  { state: AppState }
>('journey/cloneActiveJourney', async (_, { getState }) => {
  const { activeJourney, journeys } = getState().journey;
  if (!activeJourney) {
    return;
  }
  let newName = `${activeJourney.name} (2)`;
  let i = 3;
  const names = journeys.map(j => j.name);
  while (names.includes(newName)) {
    newName = `${activeJourney.name} (${i})`;
    i++;
  }

  const newJourney = await createJourney(journeyFromTemplate(newName, activeJourney));
  return newJourney.data;
});

export const fetchJourneys = createAsyncThunk<Page<Journey>, void, { state: AppState }>(
  'journey/fetchJourneys',
  async _ => {
    const res = await getJourneys({
      active: true,
      page: 1,
      pageSize: 100,
    });
    return res.data;
  },
);

export const fetchJourneyConditionSchema = createAsyncThunk<
  JourneyState['journeyConditionsSchema'],
  void,
  { state: AppState }
>('journey/fetchJourneyConditionSchema', async _ => {
  const res = await getJourneyConditionsSchema();
  return res.data;
});

export const fetchContactPipelines = createAsyncThunk<
  JourneyState['contactPipelines'],
  void,
  { state: AppState }
>('journey/fetchContactPipelines', async _ => {
  const res = await getContactPipelines();
  return res.data;
});

export const setTestDomains = createAsyncThunk<
  JourneyState['testDomains'],
  void,
  { state: AppState }
>('journey/setTestDomains', async (_, { getState }) => {
  const testDomains = getState().journey.testDomains;
  if (testDomains.length > 0) {
    return testDomains;
  }

  const res = await getOrgAnalytics(
    formatISO(sub(Date.now(), { weeks: 1 }), { representation: 'date' }),
    formatISO(Date.now(), { representation: 'date' }),
  );

  const domains = Array.from(
    new Set(
      Object.keys(res.data.pages).map(item => {
        if (item.startsWith('localhost:3500')) {
          return 'http://localhost:3500';
        }
        return `https://${item.split('/')[0]}`;
      }),
    ),
  );

  domains.sort();
  return domains;
});

const findJourneyById = (journeys: Journey[], id: Journey['id']) =>
  journeys.find(j => j.id === id);

export const journeySlice = createSlice({
  name: 'journey',
  initialState,
  reducers: {
    activateJourney: (state, action: PayloadAction<JourneyState['activeJourneyId']>) => {
      if (state.activeJourneyId === action.payload) return;
      state.activeJourneyId = action.payload;
      let activeJourney = null;
      if (action.payload) {
        const serverJourney = findJourneyById(state.journeys, action.payload);
        if (serverJourney) {
          activeJourney = { ...serverJourney, hasChanged: false };
        } else {
          throw new Error(
            `Tried to activate journey (id=${action.payload}) but its not in our list`,
          );
        }
      }
      state.activeJourney = activeJourney;
    },
    updateActiveJourney: (state, action: PayloadAction<Journey>) => {
      if (action.payload.id !== state.activeJourneyId) {
        throw Error(
          `Attempted to update journey with (id=${action.payload.id}) but active (id=${state.activeJourneyId})`,
        );
      }
      const serverJourney = findJourneyById(state.journeys, state.activeJourneyId);
      if (serverJourney) {
        delete (action.payload as any).hasChanged; // remove flag if there
        const hasChanged = !isEqual(serverJourney, action.payload);
        // update if changed, OR if hasChanged itself has changed (update that results
        // in undo of all journey changes)
        if (hasChanged || state.activeJourney?.hasChanged !== hasChanged) {
          state.activeJourney = { ...action.payload, hasChanged };
        }
      }
    },
    undoActiveJourneyChanges: state => {
      if (state.activeJourneyId) {
        const serverJourney = findJourneyById(state.journeys, state.activeJourneyId);
        if (serverJourney) {
          state.activeJourney = { ...serverJourney, hasChanged: false };
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(saveNewJourney.pending, state => {
        state.isSavingNewJourney = true;
        state.saveNewJourneyError = null;
      })
      .addCase(saveNewJourney.fulfilled, (state, action) => {
        state.isSavingNewJourney = false;
        state.journeys = [...state.journeys, action.payload];
      })
      .addCase(saveNewJourney.rejected, (state, action) => {
        state.isSavingNewJourney = false;
        state.saveNewJourneyError = action.error.message || 'Unable to save new journey';
      });

    builder
      .addCase(saveJourney.pending, state => {
        state.isSavingJourney = true;
        state.saveJourneyError = null;
      })
      .addCase(saveJourney.fulfilled, (state, action) => {
        state.isSavingJourney = false;
        const index = state.journeys.findIndex(j => j.id === action.payload.id);
        if (index) {
          state.journeys[index] = action.payload;
        }
        // If we updated the active journey, sync it with server version
        if (state.activeJourneyId === action.payload.id && state.activeJourney) {
          // for this, let's leave 'hasChanged' as-is since the update can come from outside "active journey" realms
          state.activeJourney = {
            ...action.payload,
            hasChanged: state.activeJourney.hasChanged,
          };
        }
      })
      .addCase(saveJourney.rejected, (state, action) => {
        state.isSavingJourney = false;
        state.saveJourneyError = action.error.message || 'Unable to save journey';
      });

    builder
      .addCase(cloneActiveJourney.pending, state => {
        state.isSavingNewJourney = true;
        state.saveNewJourneyError = null;
      })
      .addCase(cloneActiveJourney.fulfilled, (state, action) => {
        state.isSavingNewJourney = false;
        if (!action.payload) return;

        if (state.journeys) {
          state.journeys = [...state.journeys, action.payload];
        } else {
          state.journeys = [action.payload];
        }

        state.activeJourneyId = action.payload.id;
        state.activeJourney = { ...action.payload, hasChanged: false };
      })
      .addCase(cloneActiveJourney.rejected, (state, action) => {
        state.isSavingNewJourney = false;
        state.saveNewJourneyError = action.error.message || 'Unable to clone journey';
      });

    builder
      .addCase(removeJourney.pending, state => {
        state.isRemovingJourney = true;
        state.removeJourneyError = null;
      })
      .addCase(removeJourney.fulfilled, (state, action) => {
        state.isRemovingJourney = false;
        remove(state.journeys, j => j.id === action.payload);
        // If we deleted the active journey, clear it
        if (state.activeJourneyId === action.payload) {
          state.activeJourney = null;
          state.activeJourneyId = null;
        }
      })
      .addCase(removeJourney.rejected, (state, action) => {
        state.isRemovingJourney = false;
        state.removeJourneyError = action.error.message || 'Unable to remove journey';
      });

    builder
      .addCase(fetchJourneys.pending, state => {
        state.isFetchingJourneys = true;
        state.fetchJourneysError = null;
      })
      .addCase(fetchJourneys.fulfilled, (state, action) => {
        state.isFetchingJourneys = false;
        state.hasFetchedJourneys = true;
        state.journeys = action.payload.items;
      })
      .addCase(fetchJourneys.rejected, (state, action) => {
        state.isFetchingJourneys = false;
        state.fetchJourneysError = action.error.message || 'Unable to load journeys';
      });

    builder
      .addCase(fetchJourneyConditionSchema.pending, state => {
        state.isFetchingJourneyConditionsSchema = true;
        state.fetchJourneyConditionsSchemaError = null;
      })
      .addCase(fetchJourneyConditionSchema.fulfilled, (state, action) => {
        state.isFetchingJourneyConditionsSchema = false;
        state.hasFetchedJourneyConditionsSchema = true;
        state.journeyConditionsSchema = action.payload;
      })
      .addCase(fetchJourneyConditionSchema.rejected, (state, action) => {
        state.isFetchingJourneyConditionsSchema = false;
        state.fetchJourneyConditionsSchemaError =
          action.error.message || 'Unable to load conditions schema';
      });

    builder
      .addCase(fetchContactPipelines.pending, state => {
        state.isFetchingContactPipelines = true;
      })
      .addCase(fetchContactPipelines.fulfilled, (state, action) => {
        state.isFetchingContactPipelines = false;
        state.contactPipelines = action.payload;
      })
      .addCase(fetchContactPipelines.rejected, state => {
        state.isFetchingContactPipelines = false;
        state.contactPipelines = [];
      });

    builder
      .addCase(saveActiveJourney.pending, state => {
        state.isSavingActiveJourney = true;
        state.saveActiveJourneyError = null;
        if (state.activeJourney) {
          state.activeJourney.hasChanged = false; // optimistic clear
        }
      })
      .addCase(saveActiveJourney.fulfilled, (state, action) => {
        state.isSavingActiveJourney = false;
        const activeIndex = state.journeys.findIndex(j => j.id === state.activeJourneyId);
        if (action.payload) {
          state.journeys[activeIndex] = action.payload;
          state.activeJourney = { ...action.payload, hasChanged: false };
        }
      })
      .addCase(saveActiveJourney.rejected, (state, action) => {
        state.isSavingActiveJourney = false;
        state.saveActiveJourneyError =
          action.error.message || 'Unable to save active journey';
        if (state.activeJourney) {
          state.activeJourney.hasChanged = true; // reset
        }
      });

    builder.addCase(setTestDomains.fulfilled, (state, action) => {
      state.testDomains = action.payload;
    });
  },
});

export const selectJourneyConditionsSchema = createSelector(
  (s: AppState) => s.journey.journeyConditionsSchema,
  (s: AppState) => s.journey.activeJourney,
  (schema, journey) => {
    if (!journey || !journey.latestVersion || !schema) {
      throw Error(
        'Cannot select condition schema without active journey and/or loaded conditions schema',
      );
    }

    const graph = journey.latestVersion.graph;
    const stepLabelMap = makeStepLabelMap(graph, graph.root);
    const variables = [...schema.variables];
    // Remove all server-sent journey question choice entries
    const withoutJourneyVariables = variables.filter(v => {
      return v.name != BusinessRulesRuleName.question_choice;
    });
    // Add in journey question choice entries based on current state
    const questionChoiceVariable = {
      fieldType: BusinessRulesVariableType.select,
      label: 'Question choice',
      name: BusinessRulesRuleName.question_choice,
      options: Object.entries(graph.nodes)
        .filter(([_, node]) => {
          return (
            node.type === JourneyGraphNodeType.question &&
            (node.data as JourneyGraphNodeQuestion).type === JourneyQuestionType.choice
          );
        })
        .map(([nodeId, node]): BusinessRulesVariableOption[] => {
          const data = node.data as JourneyGraphNodeQuestion<JourneyQuestionType.choice>;
          return data.config.options.map(o => {
            // Note we use label for value if its not set
            const value = o.value || o.label;
            const labelAndValue = o.value ? `${o.label} (${o.value})` : o.label;
            return {
              label: `${stepLabelMap[nodeId]} - ${labelAndValue}`,
              name: `${nodeId}:${value}`,
            };
          });
        })
        .flat(),
    };
    const withJourneyVariables: BusinessRulesConditionsSchemaVariable[] = [
      ...withoutJourneyVariables,
      questionChoiceVariable,
    ];

    return {
      schemaWithJourneyVariables: { ...schema, variables: withJourneyVariables },
      schemaWithoutJourneyVariables: { ...schema, variables: withoutJourneyVariables },
    };
  },
);

/**
 * Hook for common data/functions for editing an active journey.
 * It will throw if there is no active journey.
 */
export const useActiveJourney = () => {
  const dispatch = useAppDispatch();
  const journey = useAppSelector(s => s.journey.activeJourney);
  const conditionsSchema = useAppSelector(selectJourneyConditionsSchema);
  if (!journey) {
    throw Error('Tried to use active journey, but no journey active');
  }

  // Save active journey to server
  const saveJourney = useCallback(async () => {
    unwrapResult(await dispatch(saveActiveJourney()));
  }, [dispatch]);

  // Set active journey back to server version
  const undoJourneyChanges = useCallback(() => {
    dispatch(undoActiveJourneyChanges());
  }, [dispatch]);

  // Set active journey content
  const setJourney = useCallback(
    (j: Journey) => {
      dispatch(updateActiveJourney({ ...journey, ...j }));
    },
    [dispatch, journey],
  );

  // Set active journey graph member
  const setGraph = useCallback(
    (g: JourneyVersion['graph']) => {
      const graph = { ...journey.latestVersion.graph, ...g };
      const latestVersion = { ...journey.latestVersion, graph };
      setJourney({ ...journey, latestVersion });
    },
    [journey, setJourney],
  );

  // Set active journey conditions member
  const setConditions = useCallback(
    (conditions: JourneyVersion['conditions']) => {
      const latestVersion = { ...journey.latestVersion, conditions };
      setJourney({ ...journey, latestVersion });
    },
    [journey, setJourney],
  );

  return {
    journey,
    graph: journey.latestVersion.graph,
    saveJourney,
    undoJourneyChanges,
    setJourney,
    setGraph,
    setConditions,
    conditionsSchema,
  };
};

export const {
  activateJourney,
  updateActiveJourney,
  undoActiveJourneyChanges,
} = journeySlice.actions;
export default journeySlice.reducer;
